import { FORMAT, dayjs } from 'utils/date';
import { getFeatureFlags } from 'utils/feature-flags';
import type { DateString } from 'utils/date-string';
import type { MealDealErrorCode } from 'redux/modules/meal-deals/types';
import {
  MEAL_DEAL_BRANCH_NOT_APPLICABLE_ERROR_CODE,
  MEAL_DEAL_OFFER_NOT_ACTIVE_ERROR_CODE,
  MEAL_DEAL_ONLY_CONTENT_AVAILABLE_ERROR_CODE,
} from 'redux/modules/meal-deals/constants';
import {
  ENDING_SOON_CAUSE,
  ENDING_TODAY_CAUSE,
  INVALID_BRANCH_CAUSE,
  INVALID_SLOT_CAUSE,
} from 'components/MealDealPage/constants';

export type MealDealDateDrivenConfig = {
  alertCause?: symbol;
  alertTitle?: string;
  alertMessage?: string;
  comingSoonMessage?: string;
  isNowAfterEndDate: boolean;
  isNowBeforeStartDate: boolean;
  isSlotBooked: boolean;
  showAlert: boolean;
  showBuilder: boolean;
  showGrid: boolean;
};

const INVALID_SLOT_TITLE = 'This offer is not available with your slot';
const ENDING_SOON_TITLE = 'This offer must end soon';
const ENDING_TODAY_TITLE = 'This offer ends today';

const INVALID_SLOT_MESSAGE =
  'This offer is available for delivery or click and collect for dates outside of your selected slot';
const ENDING_TODAY_MESSAGE =
  'It is not available for delivery from your local Waitrose. It may be available in store.';

const DEFAULT_MEAL_DEAL_DATE_DRIVEN_CONFIG: MealDealDateDrivenConfig = {
  showAlert: false,
  showBuilder: false,
  showGrid: true,
  isNowBeforeStartDate: false,
  isNowAfterEndDate: false,
  isSlotBooked: false,
};

const isOnlyContentAvailable = (errorCode: MealDealErrorCode | undefined) =>
  errorCode === MEAL_DEAL_ONLY_CONTENT_AVAILABLE_ERROR_CODE;

const isMealDealBranchNotApplicable = (errorCode: MealDealErrorCode | undefined) =>
  errorCode === MEAL_DEAL_BRANCH_NOT_APPLICABLE_ERROR_CODE;

const isMealDealSlotInvalid = (errorCode: MealDealErrorCode | undefined) =>
  errorCode === MEAL_DEAL_OFFER_NOT_ACTIVE_ERROR_CODE;

const getMealDealEndDate = (endDate: DateString): dayjs.Dayjs => {
  const {
    browse_mealDealEndDateToday: endDateToday,
    browse_mealDealEndDate1Day: endDate1Day,
    browse_mealDealEndDate2Day: endDate2Day,
  } = getFeatureFlags();

  if (endDateToday) {
    return dayjs().tz();
  }

  if (endDate1Day) {
    return dayjs().tz().add(1, 'day');
  }

  if (endDate2Day) {
    return dayjs().tz().add(2, 'day');
  }

  return dayjs(endDate).tz();
};

const getEndingSoonMessage = (endDate: dayjs.Dayjs | undefined) =>
  `${
    endDate ? `Book a collection or delivery before ${endDate.format(FORMAT.LONG_DAY_MONTH)}` : ''
  }`;

const getComingSoonMessage = (startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
  const isSameYear = startDate.year() === endDate.year();

  return `Offer applies to delivery/collection slots from
    ${startDate.format(FORMAT.DAY_MONTH)}${isSameYear ? '' : ` ${startDate.format(FORMAT.YEAR)}`} to
    ${endDate.format(FORMAT.LONG_2DAY_MONTH_YEAR)}.`;
};

const getAlert = ({
  endsToday,
  invalidSlotBooked,
  parsedEndDate,
}: {
  endsToday: boolean;
  invalidSlotBooked: boolean;
  parsedEndDate: dayjs.Dayjs;
}) => {
  if (invalidSlotBooked) {
    return {
      alertCause: INVALID_SLOT_CAUSE,
      alertMessage: INVALID_SLOT_MESSAGE,
      alertTitle: INVALID_SLOT_TITLE,
    };
  }

  if (endsToday) {
    return {
      alertCause: ENDING_TODAY_CAUSE,
      alertTitle: ENDING_TODAY_TITLE,
      alertMessage: ENDING_TODAY_MESSAGE,
    };
  }

  return {
    alertCause: ENDING_SOON_CAUSE,
    alertMessage: getEndingSoonMessage(parsedEndDate),
    alertTitle: ENDING_SOON_TITLE,
  };
};

export const getMealDealDateDrivenConfig = ({
  endDate,
  errorCode,
  slotDate,
  startDate,
}: {
  endDate?: DateString;
  errorCode?: MealDealErrorCode;
  slotDate?: DateString;
  startDate?: DateString;
}): MealDealDateDrivenConfig => {
  if (isOnlyContentAvailable(errorCode)) {
    return {
      ...DEFAULT_MEAL_DEAL_DATE_DRIVEN_CONFIG,
      showAlert: false,
      showBuilder: false,
      showGrid: false,
    };
  }

  if (isMealDealBranchNotApplicable(errorCode)) {
    return {
      ...DEFAULT_MEAL_DEAL_DATE_DRIVEN_CONFIG,
      alertCause: INVALID_BRANCH_CAUSE,
      alertMessage: 'This offer applies to specific stores, sorry for the inconvenience',
      alertTitle: 'This offer is unavailable at your store',
      showAlert: true,
      showBuilder: false,
      showGrid: false,
    };
  }

  if (isMealDealSlotInvalid(errorCode)) {
    return {
      ...DEFAULT_MEAL_DEAL_DATE_DRIVEN_CONFIG,
      alertCause: INVALID_SLOT_CAUSE,
      alertMessage: INVALID_SLOT_MESSAGE,
      alertTitle: INVALID_SLOT_TITLE,
      showAlert: true,
      showBuilder: false,
      showGrid: false,
    };
  }

  if (!startDate || !endDate) {
    return DEFAULT_MEAL_DEAL_DATE_DRIVEN_CONFIG;
  }

  const todaysDate = dayjs().tz().startOf('day');
  const parsedStartDate = dayjs(startDate).tz();
  const parsedEndDate = getMealDealEndDate(endDate);

  const isNowBeforeStartDate = todaysDate.isBefore(parsedStartDate, 'day');
  const isNowSameOrAfterEndDate = todaysDate.isSameOrAfter(parsedEndDate, 'day');
  const isNowAfterEndDate = todaysDate.isAfter(parsedEndDate, 'day');

  const parsedSlotDate = slotDate ? dayjs(slotDate).tz() : null;
  const isSlotBooked = !!parsedSlotDate;

  const isSlotAfterStartDate =
    !startDate || !isSlotBooked || parsedSlotDate.isSameOrAfter(parsedStartDate, 'day');
  const isSlotBeforeEndDate =
    !endDate || !isSlotBooked || parsedSlotDate.isSameOrBefore(parsedEndDate, 'day');
  const isSlotValidForMealDeal = isSlotAfterStartDate && isSlotBeforeEndDate;

  const showGrid =
    (!isNowBeforeStartDate && !isNowAfterEndDate) || (isSlotBooked && isSlotValidForMealDeal);

  const endsIn2DaysOrLess = parsedEndDate
    ? todaysDate.isBefore(parsedEndDate, 'day') &&
      todaysDate.isSameOrAfter(parsedEndDate.subtract(2, 'day'), 'day')
    : false;

  const endsToday = parsedEndDate ? todaysDate.isSame(parsedEndDate, 'day') : false;

  const invalidSlotBooked = isSlotBooked && !isSlotValidForMealDeal;
  const noSlotAndEndingSoon = !isSlotBooked && (endsIn2DaysOrLess || endsToday);
  const noSlotOrInvalidSlotBooked = !isSlotBooked || invalidSlotBooked;

  const showAlert = invalidSlotBooked || noSlotAndEndingSoon;

  const showBuilder =
    !isNowSameOrAfterEndDate && (!isNowBeforeStartDate || !noSlotOrInvalidSlotBooked);

  if (!showAlert) {
    return {
      comingSoonMessage: getComingSoonMessage(parsedStartDate, parsedEndDate),
      isNowAfterEndDate,
      isNowBeforeStartDate,
      isSlotBooked,
      showAlert,
      showBuilder,
      showGrid,
    };
  }

  const { alertCause, alertMessage, alertTitle } = getAlert({
    endsToday,
    invalidSlotBooked,
    parsedEndDate,
  });

  return {
    alertCause,
    alertTitle,
    alertMessage,
    comingSoonMessage: getComingSoonMessage(parsedStartDate, parsedEndDate),
    isNowAfterEndDate,
    isNowBeforeStartDate,
    isSlotBooked,
    showAlert,
    showBuilder,
    showGrid,
  };
};
